import type { Permesso, Sessione } from './sessione';
import { puo } from './sessione';

/*  LE SCHERMATE E LE CINQUE MACROAREE.

    Fino al 20/09/2026 il menu era una riga sola con tredici voci: tutto l'applicativo
    sempre davanti, e niente che dicesse a un tecnico dei preventivi che «Bolle» non e'
    roba sua. Chiesto da Andrea — e prima da Galizzi, in riunione il 14/09 — che dopo il
    login ci siano delle icone come su un telefono, una per macroarea, e che dentro
    l'area il menu mostri solo le voci di quell'area.

    Le due tabelle stanno qui e non dentro App perche' sono dati, e dei dati si puo'
    scrivere una prova che gira senza montare una schermata: `menu.test.ts` controlla che
    ogni voce abbia un'area che esiste e che nessuna area resti senza voci per sbaglio.
*/

export type Vista =
  | 'home'
  | 'preventivi'
  | 'commesse'
  | 'articoli'
  | 'richieste'
  | 'dordinare'
  | 'ordini'
  | 'carichi'
  | 'subappalti'
  | 'scontrino'
  | 'incassi'
  | 'cassa'
  | 'storico'
  | 'scontrinimiei'
  | 'rapportini'
  | 'rapportino'
  | 'listini'
  | 'testi'
  | 'predefiniti'
  | 'utenze'
  | 'registro';

export type ChiaveArea =
  | 'preventivazione'
  | 'commesse'
  | 'acquisti'
  | 'anagrafiche'
  | 'assistenza'
  | 'contabilita'
  | 'parametri';

export interface Area {
  chiave: ChiaveArea;
  etichetta: string;
  /** Una riga sotto il nome: cosa si fa qui dentro, per chi apre l'applicativo la prima volta. */
  cosa: string;
  /*  L'area prevista e non ancora costruita. L'icona si vede ed e' spenta: dice che il
      disegno la comprende, invece di lasciare un buco che sembra una dimenticanza.
      Il giorno che arriva la prima schermata, questa riga si toglie — e la prova lo
      pretende, perche' un'area con delle voci e ancora marcata «in arrivo» sarebbe una
      schermata raggiungibile da un'icona che non si clicca.  */
  inArrivo?: boolean;
}

export const AREE: Area[] = [
  {
    chiave: 'preventivazione',
    etichetta: 'Preventivazione',
    cosa: 'Offerte al cliente, revisioni e i testi che ci vanno dentro',
  },
  {
    chiave: 'commesse',
    etichetta: 'Commesse',
    cosa: 'Il lavoro accettato: costi, consegne e ore dei rapportini',
  },
  {
    chiave: 'acquisti',
    etichetta: 'Acquisti',
    cosa: 'Richieste, ordini ai fornitori e merce che arriva',
  },
  {
    chiave: 'anagrafiche',
    etichetta: 'Anagrafiche',
    cosa: 'Articoli e listini. Clienti e fornitori restano in Mago',
  },
  {
    chiave: 'assistenza',
    etichetta: 'Assistenza caldaie',
    cosa: 'Scontrini degli interventi e incassi dei tecnici',
  },
  {
    chiave: 'contabilita',
    /*  «Amministrazione» dal 22/10/2026: la prima schermata e' la cassa virtuale di
        Novara («la sezione della cassa va messa in amministrazione»). Fatturato e
        scadenze arriveranno qui.  */
    etichetta: 'Amministrazione',
    cosa: 'Cassa di Novara; poi fatturato e scadenze',
  },
  {
    chiave: 'parametri',
    etichetta: 'Parametri',
    cosa: 'Utenze, permessi e il registro di chi ha cambiato cosa',
  },
];

/*  Le voci di menu, con il permesso che ognuna pretende e l'area in cui sta.

    Sono una tabella e non tredici blocchi di JSX perché permesso, etichetta e area devono
    stare sulla stessa riga: scritti in due posti, il giorno in cui si aggiunge una
    schermata si aggiunge il bottone e si dimentica il controllo. Il permesso è quello
    dell'endpoint che la schermata chiama per prima — la griglia — quindi una voce che si
    vede è una voce che si apre. Prima di questo, un operaio vedeva «Ordini» e prendeva un
    errore.  */
export interface VoceMenu {
  vista: Vista;
  etichetta: string;
  permesso: Permesso;
  area: ChiaveArea;
}

export const VOCI: VoceMenu[] = [
  { vista: 'preventivi', etichetta: 'Preventivi', permesso: 'VediPreventivi', area: 'preventivazione' },
  /*  Il permesso è quello di redigere e non quello di gestire: chi compila un preventivo
      deve poter leggere per intero una clausola prima di spuntarla, e la schermata si
      apre in sola lettura per chi non la può riscrivere.  */
  { vista: 'testi', etichetta: 'Testi offerta', permesso: 'RedigiPreventivi', area: 'preventivazione' },

  /*  Il permesso e' quello di VEDERE i preventivi e non quello di gestirli, che e'
      dell'amministratore: «perche' questo nasce col 18 e quello col 25» e' una domanda a
      cui chi compila deve poter rispondere da solo, e senza questa schermata l'unica
      risposta era chiedere a noi. La schermata si apre in sola lettura per chi non li
      puo' cambiare, come quella dei testi.  */
  { vista: 'predefiniti', etichetta: 'Valori di partenza', permesso: 'VediPreventivi', area: 'preventivazione' },

  { vista: 'commesse', etichetta: 'Commesse', permesso: 'VediCosti', area: 'commesse' },
  /*  L'elenco d'ufficio pretende il permesso di approvare e non quello di vedere, che
      l'operaio ha: è la coda delle approvazioni, e finché non si può filtrare «i miei»
      mostrarla a chi ci lavora vorrebbe dire mostrargli le ore e i costi dei colleghi.  */
  { vista: 'rapportini', etichetta: 'Rapportini', permesso: 'ApprovaRapportini', area: 'commesse' },
  { vista: 'rapportino', etichetta: 'Nuovo', permesso: 'CompilaRapportini', area: 'commesse' },

  { vista: 'richieste', etichetta: 'Richieste', permesso: 'VediRichieste', area: 'acquisti' },
  { vista: 'dordinare', etichetta: 'Da ordinare', permesso: 'EmettiOrdini', area: 'acquisti' },
  { vista: 'ordini', etichetta: 'Ordini', permesso: 'VediOrdini', area: 'acquisti' },
  { vista: 'carichi', etichetta: 'Bolle', permesso: 'VediCarichi', area: 'acquisti' },
  /*  Gli ordini di subappalto, sezione loro (16/10/2026): un lavoro affidato a un'impresa
      non e' materiale da ricevere con una bolla. Stesso permesso degli ordini.  */
  { vista: 'subappalti', etichetta: 'Subappalti', permesso: 'VediOrdini', area: 'acquisti' },

  { vista: 'articoli', etichetta: 'Articoli', permesso: 'VediArticoli', area: 'anagrafiche' },
  /*  Guardare com'è andata l'importazione è di chi guarda gli articoli, non di chi
      carica i listini: chi si accorge che un prezzo non torna vuole sapere da dove
      viene, e quasi mai è la stessa persona che mette i file nella cartella.  */
  { vista: 'listini', etichetta: 'Listini', permesso: 'VediArticoli', area: 'anagrafiche' },

  /*  Gli scontrini dei tecnici caldaie (22/10/2026). Chi incassa scrive lo scontrino e
      vede i propri incassi; la cassa di Novara e' di chi la tiene, non di chi incassa.  */
  { vista: 'scontrino', etichetta: 'Nuovo scontrino', permesso: 'EmettiScontrini', area: 'assistenza' },
  { vista: 'incassi', etichetta: 'I miei incassi', permesso: 'EmettiScontrini', area: 'assistenza' },
  { vista: 'scontrinimiei', etichetta: 'I miei scontrini', permesso: 'EmettiScontrini', area: 'assistenza' },
  /*  La cassa di Novara sta in Amministrazione (22/10/2026): e' di chi tiene i soldi,
      non del lavoro sulle caldaie. Dal 23/10 GestisciCassa ce l'ha solo l'amministratore:
      «l'ufficio non deve vedere nulla della parte di amministrazione».  */
  { vista: 'cassa', etichetta: 'Cassa Novara', permesso: 'GestisciCassa', area: 'contabilita' },
  { vista: 'storico', etichetta: 'Storico scontrini', permesso: 'GestisciCassa', area: 'contabilita' },

  { vista: 'utenze', etichetta: 'Utenze', permesso: 'GestisciUtenti', area: 'parametri' },
  { vista: 'registro', etichetta: 'Registro', permesso: 'VediRegistro', area: 'parametri' },
];

/** Le voci che questa persona può aprire, nell'ordine del menu. */
export const vociVisibili = (sessione: Sessione): VoceMenu[] =>
  VOCI.filter((v) => puo(sessione, v.permesso));

/** Le voci di una macroarea che questa persona può aprire. */
export const vociDellArea = (area: ChiaveArea, sessione: Sessione): VoceMenu[] =>
  vociVisibili(sessione).filter((v) => v.area === area);

/*  Le icone da mettere sulla schermata iniziale.

    Un'area si vede se ha almeno una schermata che spetta a chi guarda: un operaio non
    deve trovarsi davanti cinque icone di cui quattro danno errore. Le aree «in arrivo»
    si vedono comunque, spente: dicono cosa ci sarà, e il giorno che arriva la prima
    schermata l'icona si accende da sola.  */
export const areeVisibili = (sessione: Sessione): Area[] =>
  AREE.filter((a) => a.inArrivo === true || vociDellArea(a.chiave, sessione).length > 0);

/** In che area sta una schermata. `null` per la pagina iniziale, che non sta in nessuna. */
export const areaDiVista = (vista: Vista): ChiaveArea | null =>
  VOCI.find((v) => v.vista === vista)?.area ?? null;

/** Dove si entra cliccando l'icona di un'area: la sua prima schermata aperta a chi clicca. */
export const primaVistaDellArea = (area: ChiaveArea, sessione: Sessione): Vista | null =>
  vociDellArea(area, sessione)[0]?.vista ?? null;

/*  L'ancora e' l'indirizzo di una schermata: `#ordini` deve poter essere incollato in una
    mail e aprire gli ordini. «rapportini» va provato prima di «rapportin», perche' la
    schermata d'ufficio e quella del telefono hanno nomi che si contengono.

    Senza ancora si arriva alla pagina iniziale, che e' il punto di partenza da quando
    esistono le macroaree: prima era l'elenco dei preventivi, che per un operaio era una
    schermata che non gli spetta.  */
export function vistaDaAncora(ancora: string): Vista {
  if (ancora.includes('scontrinimiei')) return 'scontrinimiei';
  if (ancora.includes('storico')) return 'storico';
  if (ancora.includes('scontrin')) return 'scontrino';
  if (ancora.includes('incass')) return 'incassi';
  if (ancora.includes('cassa')) return 'cassa';
  if (ancora.includes('registro')) return 'registro';
  if (ancora.includes('subappalt')) return 'subappalti';
  if (ancora.includes('utenz')) return 'utenze';
  if (ancora.includes('rapportini')) return 'rapportini';
  if (ancora.includes('rapportin')) return 'rapportino';
  if (ancora.includes('commess')) return 'commesse';
  if (ancora.includes('articol')) return 'articoli';
  if (ancora.includes('listin')) return 'listini';
  if (ancora.includes('testi')) return 'testi';
  if (ancora.includes('predefiniti')) return 'predefiniti';
  if (ancora.includes('richiest')) return 'richieste';
  if (ancora.includes('dordinare')) return 'dordinare';
  if (ancora.includes('ordini')) return 'ordini';
  if (ancora.includes('carich')) return 'carichi';
  if (ancora.includes('preventiv')) return 'preventivi';
  return 'home';
}
